import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  UserIcon, 
  Bars3Icon, 
  XMarkIcon,
  MagnifyingGlassIcon,
  ChevronRightIcon,
  CubeIcon,
  FolderIcon,
  PaintBrushIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import CartButton from './CartButton';
import CartPopup from './CartPopup';
import CustomButton from './CustomButton'; 
import LoginModal from './LoginModal';
import UserMenu from './UserMenu';
import AIProcessingBubble from './AIProcessingBubble';
import { navigationItems, getCurrentUser, logoutUser, Category } from '../data/mockData'; 
import { request } from '../utils/request';

const Navbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(getCurrentUser());
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const [aiProcessedCount, setAiProcessedCount] = useState(0);
  const [couponMessage, setCouponMessage] = useState('');
  
  // 分类相关状态
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  
  // 搜索相关状态
  const [searchQuery, setSearchQuery] = useState('');

  const [searchSuggestions, setSearchSuggestions] = useState<any[]>([]);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedSuggestion, setSelectedSuggestion] = useState<any | null>(null);
  const hasAiParam = new URLSearchParams(location.search).has('ai');

  // 读取本地缓存的最近一次建议，显示为输入框内的标签
  useEffect(() => {
    try {
      const cached = localStorage.getItem('lastSearchSuggestion');
      if (cached) {
        const parsed = JSON.parse(cached);
        setSelectedSuggestion(parsed);
      }
    } catch (e) {
      console.warn('Failed to read lastSearchSuggestion:', e);
    }
  }, []);

  // 当路由变化时也同步一次缓存，确保跳转后无需刷新即可显示标签
  useEffect(() => {
    try {
      const cached = localStorage.getItem('lastSearchSuggestion');
      if (cached) {
        const parsed = JSON.parse(cached);
        setSelectedSuggestion(parsed);
      } else {
        setSelectedSuggestion(null);
      }
    } catch {}
  }, [location.pathname, location.search]);

  const clearSelectedSuggestion = () => {
    try {
      localStorage.removeItem('lastSearchSuggestion');
    } catch {}
    setSelectedSuggestion(null);
  };

  // 加载分类数据
  const loadCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const response: any = await request({
        url: 'categories',
        method: 'GET'
      });
      
      if (response.success) {
        setCategories(response.result);
      }
    } catch (error) {
      console.error('Load categories error:', error);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  // 搜索建议（产品、分类、设计）
  const searchProducts = async (query: string) => {
    if (!query.trim()) {
      setSearchSuggestions([]);
      setIsSearchDropdownOpen(false);
      return;
    }

    setIsSearchLoading(true);
    try {
      const response: any = await request({
        method: 'GET',
        url: `products/suggestion?keyword=${encodeURIComponent(query)}`
      }); 
      if (response.code === 200 && response.result) {
        setSearchSuggestions(response.result);
        setIsSearchDropdownOpen(true);
      } else {
        setSearchSuggestions([]);
        setIsSearchDropdownOpen(false);
      }
    } catch (error) {
      console.error('搜索建议失败:', error);
      setSearchSuggestions([]);
      setIsSearchDropdownOpen(false);
    } finally {
      setIsSearchLoading(false);
    }
  };

  // 处理搜索输入变化
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // 清除之前的定时器
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // 设置新的定时器，延迟搜索
    searchTimeoutRef.current = setTimeout(() => {
      searchProducts(value);
    }, 300);
  };

  // 处理搜索提交
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setIsSearchDropdownOpen(false);
    }
  };

  // 处理建议项点击
  const handleSuggestionClick = (suggestion: any) => {
    console.log('suggestion........', JSON.stringify(suggestion));
    try {
      // 将选择的建议写入本地缓存，便于后续页面使用
      const payload = { ...suggestion, timestamp: Date.now() };
      localStorage.setItem('lastSearchSuggestion', JSON.stringify(payload));
    } catch (e) {
      console.warn('Failed to cache suggestion:', e);
    }
    // 立即更新当前组件内的标签显示，无需刷新
    setSelectedSuggestion(suggestion);
    if (suggestion.type === 'product') { 
      const productId = suggestion.id.replace(/^p_/, ''); 
      navigate(`/products?type=design&productId=${productId}`);
    } else if (suggestion.type === 'category') { 
      // 提取分类ID（移除 c_ 前缀）
      const categoryId = suggestion.id.replace(/^c_/, '');
      navigate(`/products?category=${categoryId}`);
    } else if (suggestion.type === 'design') {  
      navigate(`/products?type=design&tag=${encodeURIComponent(suggestion.name)}`);
    }
    setSearchQuery('');
    setIsSearchDropdownOpen(false);
  };

  // 关闭搜索下拉框
  const closeSearchDropdown = () => {
    setIsSearchDropdownOpen(false);
  };

  // Check for coupon in URL
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const couponCode = searchParams.get('coupon');

    if (couponCode) {
      try {
        const cachedCoupon = localStorage.getItem('COUPON');
        if (cachedCoupon !== couponCode) {
          localStorage.setItem('COUPON', couponCode);
          setCouponMessage("You seem to have got a coupon, start shopping now!");
          setTimeout(() => {
            setCouponMessage('');
          }, 5000);
        }
      } catch (e) {
        console.warn('Failed to process coupon:', e);
      }
    }
  }, [location.search]);

  // Load categories on component mount
  useEffect(() => {
    loadCategories();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedDesktopDropdown = dropdownRef.current && dropdownRef.current.contains(event.target as Node);
      const clickedMobileMenu = mobileMenuRef.current && mobileMenuRef.current.contains(event.target as Node);

      if (!clickedDesktopDropdown && !clickedMobileMenu) {
        setActiveDropdown(null);
      }
      // 关闭搜索下拉框
      const clickedInsideDropdown = searchDropdownRef.current && searchDropdownRef.current.contains(event.target as Node);
      if (searchInputRef.current && !searchInputRef.current.contains(event.target as Node) && !clickedInsideDropdown) {
        closeSearchDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // 监听全局登录弹窗事件，支持在其他组件中触发登录窗口
  useEffect(() => {
    const openLoginHandler = (_e: Event) => setIsLoginOpen(true);
    window.addEventListener('openLoginModal', openLoginHandler as EventListener);
    return () => {
      window.removeEventListener('openLoginModal', openLoginHandler as EventListener);
    };
  }, []);

  // Hide navbar when on /custom/* routes (must be after all hooks)
  if (location.pathname.startsWith('/custom')) {
    return null;
  }

  const handleDropdownToggle = (categoryName: string) => {
    setActiveDropdown(activeDropdown === categoryName ? null : categoryName);
  };

  const handleCartToggle = () => {
    setIsCartOpen(!isCartOpen);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };
 

  const handleLoginToggle = () => {
    setIsLoginOpen(!isLoginOpen);
  };

  const closeLogin = () => {
    setIsLoginOpen(false);
    // 更新当前用户状态
    setCurrentUser(getCurrentUser());
  };

  const handleLogout = () => {
    logoutUser();
    setCurrentUser(null);
    setIsUserMenuOpen(false);
  };

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeUserMenu = () => {
    setIsUserMenuOpen(false);
  };

  return (
    <div className="bg-white shadow-sm sticky top-0 z-[999]">
      {couponMessage && (
        <div className="fixed top-20 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-[1001] flex items-center animate-fade-in-down">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
          </svg>
          {couponMessage}
        </div>
      )}
      {/* AI Processing Bubble plugin as top floating overlay */}
      <AIProcessingBubble className="fixed top-16 right-4 z-[1000] inline-block w-fit" />
      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0">
            <img src="/images/banner-logo.jpeg" alt="GiftMkr" className="h-12 sm:h-[60px] w-auto" />
          </Link>

          {/* Search Bar - Hidden on mobile */}
          <div className="hidden md:flex flex-1 max-w-2xl mx-8">
            <form onSubmit={handleSearchSubmit} className="relative w-full">
              {/* 已选择的建议以标签形式显示在输入框内 */}
              {selectedSuggestion && (
                <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center space-x-2 bg-gray-100 border border-gray-200 rounded-full px-2 py-1">
                  {selectedSuggestion.type === 'product' && <CubeIcon className="w-4 h-4 text-gray-600" />}
                  {selectedSuggestion.type === 'category' && <FolderIcon className="w-4 h-4 text-gray-600" />}
                  {selectedSuggestion.type === 'design' && <PaintBrushIcon className="w-4 h-4 text-gray-600" />}
                  <span className="text-xs text-gray-700 max-w-[140px] truncate">{selectedSuggestion.name}</span>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={clearSelectedSuggestion}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Clear selected suggestion"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
              <input
                ref={searchInputRef}
                type="text"
                placeholder={selectedSuggestion ? '' : 'Search products...'}
                value={searchQuery}
                onChange={handleSearchInputChange}
                className={`w-full ${selectedSuggestion ? 'pl-28' : 'pl-4'} pr-12 py-3 border border-gray-300 rounded-full focus:ring-2 focus:border-transparent text-gray-700`}
                style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                onClick={() => searchProducts('new')}
                onFocus={(e) => e.currentTarget.style.setProperty('--tw-ring-color', '#2e3a47')}
              />
              <button 
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white p-2 rounded-full transition-colors"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
              </button>
              
              {/* Search Suggestions Dropdown */}
              {isSearchDropdownOpen && (
                <div ref={searchDropdownRef} className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
                  {isSearchLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderBottomColor: '#2e3a47' }}></div>
                      <p className="mt-2 text-sm">Searching...</p>
                    </div>
                  ) : searchSuggestions.length > 0 ? (
                    <div className="py-2">
                      {searchSuggestions.map((suggestion) => (
                        <button
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          key={suggestion.id}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                              {suggestion.type === 'product' && <CubeIcon className="w-5 h-5 text-gray-600" />}
                              {suggestion.type === 'category' && <FolderIcon className="w-5 h-5 text-gray-600" />}
                              {suggestion.type === 'design' && <PaintBrushIcon className="w-5 h-5 text-gray-600" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {suggestion.name}
                                </p>
                                {suggestion.tagName && (
                                  <span 
                                    className="text-xs px-2 py-0.5 rounded-full text-white flex-shrink-0"
                                    style={{ backgroundColor: suggestion.tagColor || '#1890ff' }}
                                  >
                                    {suggestion.tagName}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 truncate mt-1">
                                {suggestion.description || (suggestion.type === 'category' && `${suggestion.productCount} products`)}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : searchQuery.trim() && !isSearchLoading ? (
                    <div className="p-4 text-center text-gray-500">
                      <p className="text-sm">No products found for "{searchQuery}"</p>
                    </div>
                  ) : null}
                </div>
              )}
            </form>
          </div>

          {/* Right side elements */}
          <div className="flex items-center space-x-2 sm:space-x-4 lg:space-x-6">
            {/* Desktop elements */}
            <Link 
              to="/referral-program" 
              className="hidden lg:block text-gray-700 transition-colors font-medium text-sm"
              style={{ color: 'inherit' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
              onMouseLeave={(e) => e.currentTarget.style.color = ''}
            >
              Sell on GiftMkr
            </Link>
       
            {/* User and Cart Icons */}
            <div className="flex items-center space-x-2">
              {currentUser ? (
                <button 
                  onClick={handleUserMenuToggle}
                  className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
                >
                  <UserIcon className="h-5 w-5 text-gray-600" />
                </button>
              ) : (
                <button 
                  onClick={handleLoginToggle}
                  className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                  style={{ backgroundColor: '#2e3a47' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                >
                  <UserIcon className="h-5 w-5 text-white" />
                </button>
              )}
               
              <CartButton 
                onClick={handleCartToggle}
              />
              {/* AI 处理计数按钮（模仿 CustomButton 风格）*/}
              {aiProcessedCount > 0 && (
                <button
                  type="button"
                  className="relative ml-2 p-2 text-gray-600 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                  title="AI processed items"
                >
                  <div className="w-8 h-8 border-2 border-gray-300 rounded-full flex items-center justify-center">
                    <SparklesIcon className="h-5 w-5" />
                  </div>
                  <span className="absolute -top-1 -right-1 bg-purple-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {aiProcessedCount}
                  </span>
                </button>
              )}
            </div>
            
            {/* Mobile menu button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-gray-700 transition-colors"
              style={{ color: 'inherit' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
              onMouseLeave={(e) => e.currentTarget.style.color = ''}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {!hasAiParam && (
          <div className="md:hidden pb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search invitations, gifts, and more..."
                className="w-full pl-4 pr-12 py-3 border border-gray-300 rounded-full focus:ring-2 focus:border-transparent text-gray-700"
                style={{ '--tw-ring-color': '#2e3a47' } as React.CSSProperties}
                onFocus={(e) => e.currentTarget.style.setProperty('--tw-ring-color', '#2e3a47')}
              />
              <button 
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white p-2 rounded-full transition-colors"
                style={{ backgroundColor: '#2e3a47' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
              >
                <MagnifyingGlassIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Menu - Desktop */}
      <div className="hidden md:block border-t border-gray-200 relative" ref={dropdownRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center space-x-6 lg:space-x-8 py-3 overflow-x-auto scrollbar-hide">
            {navigationItems.map((item) => (
              <div key={item.id} className="relative">
                {item.hasDropdown ? (
                  <button
                    onClick={() => handleDropdownToggle(item.id)}
                    className={`text-gray-700 transition-colors whitespace-nowrap font-medium text-sm flex items-center ${
                      activeDropdown === item.id ? 'px-3 py-2 rounded-lg' : ''
                    }`}
                    style={activeDropdown === item.id ? { color: '#2e3a47', backgroundColor: '#f0f2f5' } : {}}
                    onMouseEnter={(e) => {
                      if (activeDropdown !== item.id) {
                        e.currentTarget.style.color = '#2e3a47';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeDropdown !== item.id) {
                        e.currentTarget.style.color = '';
                      }
                    }}
                  >
                    {item.name}
                    <ChevronRightIcon className={`ml-1 h-4 w-4 transition-transform ${
                      activeDropdown === item.id ? 'rotate-90' : ''
                    }`} />
                  </button>
                ) : (
                  <Link 
                    to={item.href.startsWith('/products') ? item.href : `/products?category=${item.id}`}
                    className={`transition-colors whitespace-nowrap font-medium text-sm ${item.background ? 'px-4 py-2 rounded-lg' : ''}`}
                    style={{
                      color: item.textColor || 'inherit',
                      background: item.background || 'transparent',
                      boxShadow: item.background ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' : 'none',
                      fontWeight: item.fontWeight || undefined
                    }}
                    onMouseEnter={(e) => {
                      if (!item.textColor) {
                        e.currentTarget.style.color = '#2e3a47';
                      } else {
                        e.currentTarget.style.opacity = '0.9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!item.textColor) {
                        e.currentTarget.style.color = '';
                      } else {
                        e.currentTarget.style.opacity = '1';
                      }
                    }}
                    onClick={() => {
                      // 如果点击的是 AI Tee 并且 Create Your Own 下拉菜单是展开的，就关闭它
                      if (item.id === 'gifts' && activeDropdown === 'create-your-own') {
                        setActiveDropdown(null);
                      }
                    }}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Create Your Own Mega Menu */}
        {activeDropdown === 'create-your-own' && (
          <div className="absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Section - Categories */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {categories.map((category) => (
                    <div key={category.name} className="space-y-2">
                      <Link 
                        to={`/products?category=${category.id}&page=1`}
                        onClick={() => setActiveDropdown(null)}
                        className="block font-semibold text-gray-900 transition-colors"
                        style={{ color: 'inherit' }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
                        onMouseLeave={(e) => e.currentTarget.style.color = ''}
                      >
                        {category.name} <ChevronRightIcon className="inline h-3 w-3" />
                      </Link>  
                      <div className="space-y-1 ml-4">
                        {category.subCategories?.map((subCategory) => (
                          <Link
                            key={subCategory.name}
                            to={`/products?category=${subCategory.id}&page=1`}
                            onClick={() => setActiveDropdown(null)}
                            className="block text-sm text-gray-600 transition-colors"
                            style={{ color: 'inherit' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
                            onMouseLeave={(e) => e.currentTarget.style.color = ''}
                          >
                            {subCategory.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Right Section - Visual Examples */}
                <div className="bg-yellow-50 rounded-lg p-6"> 
                  
                  <div className="mt-6 text-center">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Create Your Own</h3>
                    <p className="text-gray-600 text-sm">Create something perfectly one-of-a-kind.</p>
                    <Link
                      to="/products"
                      onClick={() => setActiveDropdown(null)}
                      className="inline-block mt-4 text-white px-6 py-2 rounded-lg transition-colors font-medium"
                      style={{ backgroundColor: '#2e3a47' }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#3a4a57'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e3a47'}
                    >
                      Start Creating
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* More Dropdown Menu */}
        {activeDropdown === 'more' && (
          <div className="absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {navigationItems.find(item => item.id === 'more')?.subCategories?.map((subItem) => (
                  <Link
                    key={subItem.name}
                    to={subItem.href}
                    onClick={() => setActiveDropdown(null)}
                    className="block text-gray-700 transition-colors py-2"
                    style={{ color: 'inherit' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
                    onMouseLeave={(e) => e.currentTarget.style.color = ''}
                  >
                    {subItem.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Navigation Menu */}
      {isMenuOpen && (
        <div ref={mobileMenuRef} className="md:hidden border-t border-gray-200 bg-white shadow-lg">
          <div className="px-4 py-4 space-y-1 max-h-96 overflow-y-auto">
            {/* Mobile Sign in button */}
            {!currentUser && (
              <div className="pb-3 mb-3 border-b border-gray-200">
                <button 
                  onClick={handleLoginToggle}
                  className="block w-full text-white px-4 py-3 rounded-lg text-center font-medium"
                  style={{ backgroundColor: '#2e3a47' }}
                >
                  Sign in
                </button>
              </div>
            )}

            {/* Main navigation links */} 
            {navigationItems.map((item) => (
              <div key={item.id} className="border-b border-gray-100">
                {item.hasDropdown ? (
                  <>
                    <button
                      onClick={() => setActiveDropdown(activeDropdown === item.id ? null : item.id)}
                      className="w-full text-left py-3 text-gray-700 transition-colors flex items-center justify-between"
                      style={{ color: 'inherit' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
                      onMouseLeave={(e) => e.currentTarget.style.color = ''}
                    >
                      {item.name}
                      <ChevronRightIcon className={`h-4 w-4 transition-transform ${
                        activeDropdown === item.id ? 'rotate-90' : ''
                      }`} />
                    </button>
                    {activeDropdown === item.id && (
                      <div className="pl-4 pb-3 space-y-2">
                        {item.id === 'create-your-own' ? (
                          categories.map((category) => (
                            <div key={category.id} className="space-y-1">
                              <button
                                type="button"
                                className="block w-full text-left py-2 text-sm font-medium text-gray-700 transition-colors"
                                style={{ color: 'inherit' }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
                                onMouseLeave={(e) => e.currentTarget.style.color = ''}
                                onClick={() => {
                                  console.log("xxxxxxxx",category.id);
                                  setIsMenuOpen(false);
                                  setActiveDropdown(null);
                                  navigate(`/products?category=${category.id}&page=1`);
                                }}
                              >
                                {category.name}
                              </button>
                              <div className="pl-3 space-y-1">
                                {category.subCategories?.map((subCategory) => (
                                  <button
                                    key={subCategory.id}
                                    type="button"
                                    className="block w-full text-left py-1 text-sm text-gray-600 transition-colors"
                                    style={{ color: 'inherit' }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = ''}
                                    onClick={() => {
                                      setIsMenuOpen(false);
                                      setActiveDropdown(null);
                                      navigate(`/products?category=${subCategory.id}&page=1`);
                                    }}
                                  >
                                    {subCategory.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))
                        ) : (
                          item.subCategories?.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={`/products?category=${subItem.href.split('/').pop()}`}
                              className="block py-2 text-sm text-gray-600 transition-colors"
                              style={{ color: 'inherit' }}
                              onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
                              onMouseLeave={(e) => e.currentTarget.style.color = ''}
                              onClick={() => setIsMenuOpen(false)}
                            >
                              {subItem.name}
                            </Link>
                          ))
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <Link 
                    to={item.href.startsWith('/products') ? item.href : `/products?category=${item.id}`}
                    className={`block py-3 transition-colors ${item.background ? 'px-4 py-2 rounded-lg my-1' : ''}`}
                    style={{
                      color: item.textColor || 'inherit',
                      background: item.background || 'transparent',
                      fontWeight: item.fontWeight || undefined
                    }}
                    onMouseEnter={(e) => {
                      if (!item.textColor) {
                        e.currentTarget.style.color = '#2e3a47';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!item.textColor) {
                        e.currentTarget.style.color = '';
                      }
                    }}
                    onClick={() => {
                      setIsMenuOpen(false);
                      // 如果点击的是 AI Tee 并且 Create Your Own 下拉菜单是展开的，就关闭它
                      if (item.id === 'gifts' && activeDropdown === 'create-your-own') {
                        setActiveDropdown(null);
                      }
                    }}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
            
            {/* Additional mobile links */}
            <div className="pt-3 border-t border-gray-200 space-y-2"> 
              <Link 
                to="/contact" 
                className="block py-2 text-gray-700 transition-colors"
                style={{ color: 'inherit' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#2e3a47'}
                onMouseLeave={(e) => e.currentTarget.style.color = ''}
                onClick={() => setIsMenuOpen(false)}
              >
                Help & Support
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Cart Popup */}
      <CartPopup 
        isOpen={isCartOpen}
        onClose={closeCart}
      />
 
      {/* Login Modal */}
      <LoginModal 
        isOpen={isLoginOpen}
        onClose={closeLogin}
      />

      {/* User Menu */}
      {isUserMenuOpen && currentUser && (
        <UserMenu 
          user={currentUser}
          onClose={closeUserMenu}
          onLogout={() => setCurrentUser(null)}
        />
      )}
    </div>
  );
};

export default Navbar;
